import React, { useRef, useState } from 'react';
import { useDispatch } from "react-redux";
import Button from '../../components/Button';
import { UploadCloud02 } from '@untitledui/icons/UploadCloud02';
import { closeModal, openModals } from "../../redux/modal.slice";
import { useUploadDocumentMutation } from "../../api/mutations/questions.mutation"; 
import { toast } from 'react-toastify';
import Notification from '../../components/Notification';
import ModalsEnum from "../../enums/modal.enum";
import Loader from '../../components/Loader';

const UploadAndGenerateModal = () => {

    const dispatch = useDispatch();
    const [fileName, setFileName] = useState('');
    const inputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    const { mutate: uploadDocument, isPending } = useUploadDocumentMutation();
    
    const handleCloseModal = () => dispatch(closeModal());

    const handleFileSelect = (file) => {
        if (!file) return;

        const allowedExtensions = ['.pdf', '.docx', '.pptx', '.txt', '.jpg', '.jpeg', '.png'];
        const fileExt = `.${file.name.split('.').pop().toLowerCase()}`;
        if (!allowedExtensions.includes(fileExt)) {
            toast.error(<Notification text={`Invalid file format. Allowed: ${allowedExtensions.join(', ')}`} icon={'warning'} />);
            return;
        }

        setFileName(file.name);
        setSelectedFile(file);
    };

    const handleFileChange = (e) => {
        handleFileSelect(e.target.files[0]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFileSelect(e.dataTransfer.files[0]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleContinue = () => {
        if (!selectedFile) {
            toast.error(<Notification text={'Please select a file before continuing.'} icon={'warning'} />);
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        uploadDocument(formData, {
            onSuccess: (data) => {
                handleCloseModal();
                dispatch(
                    openModals({
                        modalIds: [ModalsEnum.GENERATE_YOUR_QUIZ_MODAL],
                        modalData: [
                            { 
                                id: data?.id,
                                categoryTitle: 'Uploaded Document',
                                isDocument: true
                            }
                        ]
                    })
                );
            },
            onError: (error) => {
                toast.error(<Notification text={error.message} icon={'warning'} />);
            }
        });
    };

    return (
        <>
            <div className="modal-header">
                <h3 className='mb-8'>Upload & Generate</h3>
                <p className='text-min text-color-grey'>Upload a file to generate a quiz instantly</p>
            </div>
            <div className="modal-body">
                <div
                    onClick={() => inputRef.current.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`upload-file-box ${
                        dragActive ? '' : ''
                    }`}
                    >
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".pdf, .docx, .pptx, .txt, .jpg, .jpeg, .png"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        disabled={isPending}
                    />
                    {!fileName ? (
                        <>
                            <div className="icon">
                                <UploadCloud02 size={20} />
                            </div>
                            <p><strong>Click to upload</strong> or drag and drop <br /> PDF, DOCX, PPTX, TXT, PNG, JPG or JPEG (max. 800x400px)</p>
                        </>
                    ) : (
                        <p>{fileName}</p>
                    )}
                </div>
            </div>
            <div className="modal-footer flex">
                <Button 
                    text="Cancel"
                    type="outlined"
                    className='full-width mr-12'
                    onClick={handleCloseModal}
                />
                <Button 
                    text="Continue"
                    className='full-width'
                    onClick={handleContinue}
                    disabled={isPending}
                />
            </div>
            {isPending && <Loader />}
        </>
    );
}

export default UploadAndGenerateModal;